import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { getProjectDetails, updateProjectDetails } from '../services/projects.js'

function fmtDate(d) {
  if (!d) return ''
  try {
    // Use T12:00:00 to avoid timezone edge-case off-by-one
    return new Date(d + 'T12:00:00').toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    })
  } catch { return d }
}

function ReadOnlyRow({ label, value }) {
  if (!value) return null
  return (
    <div className="aj-proj-info-row">
      <span className="aj-proj-info-label">{label}</span>
      <span className="aj-proj-info-value">{value}</span>
    </div>
  )
}

function EditableRow({ label, value, onChange }) {
  const [editing, setEditing] = useState(false)
  const inputRef = useRef(null)

  function activate() {
    setEditing(true)
    setTimeout(() => inputRef.current?.focus(), 0)
  }

  return (
    <div className="aj-proj-info-row">
      <span className="aj-proj-info-label">{label}</span>
      {editing ? (
        <input
          ref={inputRef}
          type="text"
          defaultValue={value || ''}
          className="aj-proj-info-input"
          onBlur={e => { onChange(e.target.value); setEditing(false) }}
          onKeyDown={e => { if (e.key === 'Enter') inputRef.current?.blur() }}
        />
      ) : (
        <span className="aj-proj-info-value aj-proj-info-value--editable" onClick={activate}>
          {value || <em className="aj-proj-info-empty">Not set</em>}
          <span className="aj-proj-info-edit-icon">✎</span>
        </span>
      )}
    </div>
  )
}

function DateRow({ label, value, onChange }) {
  const [editing, setEditing] = useState(false)
  const inputRef = useRef(null)

  function activate() {
    setEditing(true)
    setTimeout(() => inputRef.current?.focus(), 0)
  }

  return (
    <div className="aj-proj-info-row">
      <span className="aj-proj-info-label">{label}</span>
      {editing ? (
        <input
          ref={inputRef}
          type="date"
          defaultValue={value || ''}
          className="aj-proj-info-input aj-proj-info-input--date"
          onBlur={e => { onChange(e.target.value); setEditing(false) }}
          onKeyDown={e => { if (e.key === 'Enter') inputRef.current?.blur() }}
        />
      ) : (
        <span className="aj-proj-info-value aj-proj-info-value--editable" onClick={activate}>
          {value ? fmtDate(value) : <em className="aj-proj-info-empty">Not set</em>}
          <span className="aj-proj-info-edit-icon">✎</span>
        </span>
      )}
    </div>
  )
}

function UrlRow({ label, value, onChange }) {
  const [editing, setEditing] = useState(false)
  const inputRef = useRef(null)

  function activate() {
    setEditing(true)
    setTimeout(() => inputRef.current?.focus(), 0)
  }

  return (
    <div className="aj-proj-info-row">
      <span className="aj-proj-info-label">{label}</span>
      {editing ? (
        <input
          ref={inputRef}
          type="url"
          defaultValue={value || ''}
          className="aj-proj-info-input"
          placeholder="Paste URL…"
          onBlur={e => { onChange(e.target.value.trim()); setEditing(false) }}
          onKeyDown={e => { if (e.key === 'Enter') inputRef.current?.blur() }}
        />
      ) : (
        <span className="aj-proj-info-value aj-proj-info-url-wrap">
          {value ? (
            <>
              <a href={value} target="_blank" rel="noopener" className="aj-proj-info-link">
                Open ↗
              </a>
              <button className="aj-proj-info-edit-btn" onClick={activate} title="Edit URL">✎</button>
            </>
          ) : (
            <span className="aj-proj-info-value--editable" onClick={activate}>
              <em className="aj-proj-info-empty">Not set</em>
              <span className="aj-proj-info-edit-icon">✎</span>
            </span>
          )}
        </span>
      )}
    </div>
  )
}

export default function ProjectInfoModal() {
  const [open, setOpen] = useState(false)
  const [proj, setProj] = useState(null)
  const [loading, setLoading] = useState(false)
  const [dirty, setDirty] = useState({})
  const [saving, setSaving] = useState(false)
  const [confirmClose, setConfirmClose] = useState(false)

  useEffect(() => {
    async function onOpen(e) {
      setOpen(true)
      setLoading(true)
      setDirty({})
      setConfirmClose(false)
      const rec = await getProjectDetails(e.detail.projId)
      setProj(rec)
      setLoading(false)
    }
    window.addEventListener('open-project-info-modal', onOpen)
    return () => window.removeEventListener('open-project-info-modal', onOpen)
  }, [])

  function setField(field, value) {
    setDirty(d => ({ ...d, [field]: value }))
  }

  async function handleSave() {
    if (!proj || !Object.keys(dirty).length) return
    setSaving(true)
    await updateProjectDetails(proj.id, dirty)
    // Merge saved changes into local record optimistically
    setProj(p => ({ ...p, fields: { ...p.fields, ...dirty } }))
    setDirty({})
    setSaving(false)
  }

  function requestClose() {
    if (Object.keys(dirty).length > 0) {
      setConfirmClose(true)
    } else {
      doClose()
    }
  }

  function doClose() {
    setOpen(false)
    setProj(null)
    setDirty({})
    setConfirmClose(false)
  }

  if (!open) return null

  const f = proj?.fields || {}

  const address = f['Project Address'] || ''
  const mapsQuery = encodeURIComponent(address)

  const firstName  = Array.isArray(f['Main Client Contact First Name']) ? f['Main Client Contact First Name'][0] : f['Main Client Contact First Name'] || ''
  const lastName   = Array.isArray(f['Main Client Contact Last Name'])  ? f['Main Client Contact Last Name'][0]  : f['Main Client Contact Last Name']  || ''
  const email      = Array.isArray(f['Main Client Contact Email'])       ? f['Main Client Contact Email'][0]      : f['Main Client Contact Email']      || ''
  const clientName = Array.isArray(f['Client Name']) ? f['Client Name'][0] : f['Client Name'] || ''
  const contactName = [firstName, lastName].filter(Boolean).join(' ')

  // Live values — dirty overrides saved
  const agreementDate  = Object.prototype.hasOwnProperty.call(dirty, 'Agreement Date')         ? dirty['Agreement Date']         : (f['Agreement Date']            || '')
  const contractType   = Object.prototype.hasOwnProperty.call(dirty, 'Main Contract Type')     ? dirty['Main Contract Type']     : (f['Main Contract Type']         || '')
  const bsUrl          = Object.prototype.hasOwnProperty.call(dirty, 'Signed BS Agreement URL') ? dirty['Signed BS Agreement URL'] : (f['Signed BS Agreement URL']  || '')
  const dcUrl          = Object.prototype.hasOwnProperty.call(dirty, 'Signed DC Agreement URL') ? dirty['Signed DC Agreement URL'] : (f['Signed DC Agreement URL']  || '')
  const slackChannelId = Object.prototype.hasOwnProperty.call(dirty, 'Slack Channel ID')        ? dirty['Slack Channel ID']        : (f['Slack Channel ID']          || '')
  const coreUrl        = f['Open in Core'] || ''
  const isDirty        = Object.keys(dirty).length > 0

  return createPortal(
    <div className="aj-proj-info-overlay" onClick={confirmClose ? undefined : requestClose}>
      <div className="aj-proj-info-modal" data-modal-id="ProjectInfoModal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="aj-proj-info-header">
          <span>Project Info</span>
          <button className="aj-proj-info-close" onClick={requestClose}>✕</button>
        </div>

        {loading ? (
          <div className="aj-proj-info-loading">Loading…</div>
        ) : confirmClose ? (
          /* ── Unsaved changes warning ── */
          <div className="aj-proj-info-confirm-close">
            <p className="aj-proj-info-confirm-msg">You have unsaved changes. Discard them?</p>
            <div className="aj-proj-info-confirm-btns">
              <button className="aj-proj-info-discard-btn" onClick={doClose}>Discard changes</button>
              <button className="aj-proj-info-keep-btn" onClick={() => setConfirmClose(false)}>Keep editing</button>
            </div>
          </div>
        ) : (
          <>
            {/* Google Maps embed */}
            {address && (
              <div className="aj-proj-info-map-wrap">
                <iframe
                  src={`https://maps.google.com/maps?q=${mapsQuery}&output=embed`}
                  width="100%"
                  height="210"
                  style={{ border: 0, display: 'block' }}
                  loading="lazy"
                  title="Project location"
                />
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${mapsQuery}`}
                  target="_blank"
                  rel="noopener"
                  className="aj-proj-info-maps-link"
                >
                  Open in Google Maps ↗
                </a>
              </div>
            )}

            {/* Read-only fields */}
            <div className="aj-proj-info-section">
              <ReadOnlyRow label="Project #" value={f['Project Number']} />
              <ReadOnlyRow label="Address"   value={address} />
              <ReadOnlyRow label="Client"    value={clientName} />
              {contactName && (
                <div className="aj-proj-info-row">
                  <span className="aj-proj-info-label">Main Contact</span>
                  <span className="aj-proj-info-value">
                    {contactName}
                    {email && <span className="aj-proj-info-contact-email">{email}</span>}
                  </span>
                </div>
              )}
            </div>

            {/* Editable fields */}
            <div className="aj-proj-info-section">
              <DateRow
                label="Agreement Date"
                value={agreementDate}
                onChange={v => setField('Agreement Date', v)}
              />
              <EditableRow
                label="Contract Type"
                value={contractType}
                onChange={v => setField('Main Contract Type', v)}
              />
            </div>

            {/* Agreement URLs */}
            <div className="aj-proj-info-section">
              <UrlRow
                label="BS Agreement"
                value={bsUrl}
                onChange={v => setField('Signed BS Agreement URL', v)}
              />
              <UrlRow
                label="DC Agreement"
                value={dcUrl}
                onChange={v => setField('Signed DC Agreement URL', v)}
              />
            </div>

            {/* Integrations */}
            <div className="aj-proj-info-section">
              <EditableRow
                label="Slack Channel"
                value={slackChannelId}
                onChange={v => setField('Slack Channel ID', v)}
              />
            </div>

            {/* Footer */}
            <div className="aj-proj-info-footer">
              {coreUrl ? (
                <a href={coreUrl} target="_blank" rel="noopener" className="aj-proj-info-core-btn">
                  Open in BQE Core ↗
                </a>
              ) : <span />}
              <button
                className={`aj-proj-info-save-btn${!isDirty ? ' aj-proj-info-save-btn--idle' : ''}`}
                onClick={handleSave}
                disabled={saving || !isDirty}
              >
                {saving ? 'Saving…' : isDirty ? 'Save changes' : 'No changes'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  )
}
